import { useState } from 'react'
import { monthKeyFromT } from './engine/calendar'
import type { CashPolicyCadence, Model } from './engine/types'
import { formatCurrency, formatCurrencyWhole } from './projectionsFormat'
import { GREEN_STATUS_TAG, SUB_VALUE } from './gridClasses'

type KpiStripProps = {
  evaluated: Model | null
  lastClosedMonth: string
  onOpenCashPolicy: () => void
  policyEnabled: boolean
  amountPerPeriod: number | null
  cadence: CashPolicyCadence | null
}

type KpiPeriod = '3' | '6' | '12'

const PERIOD_OPTIONS: Array<{ value: KpiPeriod; label: string }> = [
  { value: '3', label: '3M' },
  { value: '6', label: '6M' },
  { value: '12', label: '12M' },
]

function kpiValue(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) {
    return '-'
  }
  return formatCurrency(value)
}

function periodValue(
  series: { t3: number | null; t6: number | null; t12: number | null } | undefined,
  period: KpiPeriod,
): number | null | undefined {
  if (!series) return null
  if (period === '3') return series.t3
  if (period === '6') return series.t6
  return series.t12
}

function SegmentedToggle({
  value,
  onChange,
  ariaLabel,
  nested = false,
}: {
  value: KpiPeriod
  onChange: (value: KpiPeriod) => void
  ariaLabel: string
  nested?: boolean
}) {
  const itemClass = (selected: boolean) =>
    `whitespace-nowrap rounded-md px-2 py-0.5 text-xs font-medium transition-all ${
      selected ? 'bg-white text-brand-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'
    }`
  return (
    <div
      className="inline-flex shrink-0 rounded-lg bg-gray-200 p-0.5"
      role="group"
      aria-label={ariaLabel}
      onClick={(event) => event.stopPropagation()}
    >
      {PERIOD_OPTIONS.map((opt) =>
        nested ? (
          <span
            key={opt.value}
            role="radio"
            aria-checked={value === opt.value}
            tabIndex={0}
            className={`${itemClass(value === opt.value)} cursor-pointer`}
            onClick={() => onChange(opt.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                event.stopPropagation()
                onChange(opt.value)
              }
            }}
          >
            {opt.label}
          </span>
        ) : (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            className={itemClass(value === opt.value)}
          >
            {opt.label}
          </button>
        ),
      )}
    </div>
  )
}

function KpiCard({
  title,
  series,
  period,
  onPeriodChange,
  lastClosedMonth,
}: {
  title: string
  series: { t3: number | null; t6: number | null; t12: number | null } | undefined
  period: KpiPeriod
  onPeriodChange: (period: KpiPeriod) => void
  lastClosedMonth: string
}) {
  const monthLabel = lastClosedMonth ? monthKeyFromT(lastClosedMonth, Number(period)) : null
  return (
    <div className="w-[400px] shrink-0 rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
      <div className="flex items-start justify-between gap-2">
        <span className="text-sm font-medium text-gray-900">{title}</span>
        <SegmentedToggle
          value={period}
          onChange={onPeriodChange}
          ariaLabel={`${title} period`}
        />
      </div>
      <p className="mt-2 text-xl font-semibold tabular-nums text-gray-900">
        {kpiValue(periodValue(series, period))}
      </p>
      {monthLabel ? <p className={`mt-0.5 ${SUB_VALUE}`}>{monthLabel}</p> : null}
    </div>
  )
}

function cadenceSuffix(cadence: CashPolicyCadence): string {
  return cadence === 'biweekly' ? '/ 2 wks' : '/ mo'
}

export default function KpiStrip({
  evaluated,
  lastClosedMonth,
  onOpenCashPolicy,
  policyEnabled,
  amountPerPeriod,
  cadence,
}: KpiStripProps) {
  const [netIncomePeriod, setNetIncomePeriod] = useState<KpiPeriod>('12')
  const [cashPeriod, setCashPeriod] = useState<KpiPeriod>('12')

  const ni = evaluated?.kpis.net_income
  const cash = evaluated?.kpis.cash
  const periodNum = Number(cashPeriod)
  const mmBalance = evaluated?.cash_policy?.months[periodNum - 1]?.mm_balance

  return (
    <div className="flex flex-wrap items-stretch gap-4">
      <KpiCard
        title="Net Income"
        series={ni}
        period={netIncomePeriod}
        onPeriodChange={setNetIncomePeriod}
        lastClosedMonth={lastClosedMonth}
      />
      <button
        type="button"
        aria-haspopup="dialog"
        aria-label="Open cash allocation policy"
        onClick={onOpenCashPolicy}
        className="relative w-[400px] shrink-0 rounded-lg border border-gray-200 bg-white p-3 text-left shadow-sm transition-shadow hover:shadow-md"
      >
        <div className="flex items-start justify-between gap-2">
          <span className="text-sm font-medium text-gray-900">Ending Cash</span>
          <SegmentedToggle
            value={cashPeriod}
            onChange={setCashPeriod}
            ariaLabel="Ending Cash period"
            nested
          />
        </div>
        <p className="mt-2 text-xl font-semibold tabular-nums text-gray-900">
          {kpiValue(periodValue(cash, cashPeriod))}
        </p>
        {policyEnabled && amountPerPeriod != null && cadence ? (
          <p className={`mt-1 ${SUB_VALUE}`}>
            Policy · {formatCurrencyWhole(amountPerPeriod)} {cadenceSuffix(cadence)}
            {mmBalance != null ? ` · MM ${formatCurrency(mmBalance)} @ +${cashPeriod}M` : ''}
          </p>
        ) : null}
        <div className="mt-0.5 flex items-end justify-between gap-2">
          {lastClosedMonth ? (
            <p className={SUB_VALUE}>{monthKeyFromT(lastClosedMonth, periodNum)}</p>
          ) : (
            <span />
          )}
          {policyEnabled ? (
            <span className={GREEN_STATUS_TAG}>Policy</span>
          ) : (
            <span
              className="-mr-1 -mb-1 flex h-8 min-w-8 items-center justify-center rounded-md text-base text-gray-500"
              aria-hidden="true"
            >
              →
            </span>
          )}
        </div>
      </button>
    </div>
  )
}
